"use client";

import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import Input from "@/components/ui/input";
import PrimaryButton from "@/components/ui/primary-button";
import InlineButton from "@/components/ui/inline-button";
import { searchLocation, reverseGeocode } from "./action";
import { FindCareLocationFormData } from "./action";
import { Crosshair, Search } from "react-feather";
import NoLocationNearYourSearch from "./no-location-near-your-search";
import { useWindowSize } from "@/hooks/use-window-size";
import LoadingSearch from "./loading-search";
import { cn } from "@/lib/utils";
import { getPlacePredictions, PlacePrediction } from "./places-api";
import { useDebounce } from "@/hooks/use-debounce";
import { useLocationSearchStore } from "@/store/location-search-store";

export type FindCareNearYouProps = {
  onHandleCurrentUserCoordinates: (coordinates: {
    lat: number;
    lng: number;
  }) => void;
  isInServiceArea: boolean;
  onResetNearestLocation: () => void;
};

const FindCareNearYou = ({
  isInServiceArea,
  onHandleCurrentUserCoordinates,
  onResetNearestLocation,
}: FindCareNearYouProps) => {
  const size = useWindowSize();
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const predictionsRef = useRef<HTMLDivElement>(null);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 300);

  const { cache, addToCache, cleanupCache } = useLocationSearchStore();

  const schema = z.object({
    location: z.string().min(1, { message: "Location is required" }),
  });

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = useForm<FindCareLocationFormData>({
    defaultValues: {
      location: "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FindCareLocationFormData) => {
    if (isSubmitting) return;

    onResetNearestLocation();
    try {
      setStatus("idle");
      setErrorMessage("");

      const result = await searchLocation(data);

      if (result.success && result.data) {
        const { address, lat, lng } = result.data;
        setValue("location", address);
        onHandleCurrentUserCoordinates({ lat, lng });
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage(result.error || "Failed to search location");
      }
    } catch (error) {
      console.error(error);
      setStatus("error");
      setErrorMessage("An unexpected error occurred");
    }
  };

  const handleUseCurrentLocation = () => {
    onResetNearestLocation();
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const result = await reverseGeocode(latitude, longitude);

          if (result.success && result.data) {
            setValue("location", result.data.address);
            onHandleCurrentUserCoordinates({
              lat: latitude,
              lng: longitude,
            });
            handleSubmit(onSubmit)();
          } else {
            setStatus("error");
            setErrorMessage(result.error || "Failed to get location");
          }
        } catch (error) {
          console.error("Error getting location:", error);
          setStatus("error");
          setErrorMessage("Failed to get current location");
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        setStatus("error");
        setErrorMessage("Failed to get current location");
      }
    );
  };

  useEffect(() => {
    const CACHE_DURATION = 24 * 60 * 60 * 1000;

    const fetchPredictions = async () => {
      if (debouncedSearchInput.length > 0) {
        try {
          // Check cache first
          const cachedResult = cache[debouncedSearchInput];
          const now = Date.now();

          if (cachedResult && now - cachedResult.timestamp < CACHE_DURATION) {
            setPredictions(cachedResult.predictions);
            setShowPredictions(true);
            return;
          }

          const predictions = await getPlacePredictions(debouncedSearchInput);
          setPredictions(predictions);
          setShowPredictions(true);

          addToCache(debouncedSearchInput, predictions);
        } catch (error) {
          console.error("Error fetching predictions:", error);
          setPredictions([]);
        }
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    };

    fetchPredictions();
  }, [debouncedSearchInput, cache, addToCache]);

  useEffect(() => {
    cleanupCache();
    const interval = setInterval(cleanupCache, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [cleanupCache]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue("location", value);
    setSearchInput(value);
    setActiveIndex(-1);
  };

  const handlePredictionClick = async (prediction: PlacePrediction) => {
    setValue("location", prediction.description);
    setShowPredictions(false);
    setPredictions([]);
    handleSubmit(onSubmit)();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, predictions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handlePredictionClick(predictions[activeIndex]);
    } else if (e.key === "Escape") {
      setShowPredictions(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="pointer-events-auto mx-auto flex w-full rounded-2xl bg-primary-100 p-2 shadow-[2px_6px_16px_2px_#524D4B33] md:p-4 lg:ml-0 lg:mr-auto lg:w-[532px] lg:p-6 lg:shadow-[4px_8px_24px_2px_#524D4B33]">
      <div className="my-auto flex w-full flex-col gap-2 lg:gap-6">
        <h2 className="font-montserrat text-subheading-3-500 text-neutral-600 md:text-heading-4 lg:text-heading-2">
          Find Care Near You
        </h2>
        <div className="flex flex-col gap-2">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-2"
          >
            <div className="flex gap-2">
              <div className="relative flex w-full flex-col gap-2">
                <Input
                  {...register("location", { required: true })}
                  className="flex h-10 w-full"
                  baseInputClassName="md:py-3 py-2"
                  placeholder="ZIP Code or City"
                  autoComplete="off"
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onBlur={() =>
                    setTimeout(() => setShowPredictions(false), 300)
                  }
                  onFocus={() => setShowPredictions(true)}
                  aria-label="Location search"
                  error={status === "error"}
                  errorMessage={errorMessage}
                />
                {showPredictions && predictions.length > 0 && (
                  <div
                    ref={predictionsRef}
                    className={cn(
                      "absolute left-0 z-50 max-h-60 overflow-y-auto rounded-lg bg-white",
                      "bottom-[110%] top-auto shadow-[2px_8px_24px_0px_#03030326] lg:bottom-auto lg:top-[95%]"
                    )}
                  >
                    {predictions.map((prediction, index) => (
                      <div
                        key={prediction.place_id}
                        className={cn(
                          "cursor-pointer gap-2 px-4 py-2 font-roboto text-body-1-400 ease-out",
                          "focus:border focus:border-[#9E9691] active:border-[#9E9691]",
                          index === activeIndex
                            ? "border border-[#9E9691] bg-neutral-100"
                            : "bg-white hover:bg-neutral-100"
                        )}
                        onClick={() => handlePredictionClick(prediction)}
                      >
                        {prediction.description}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <PrimaryButton
                className={cn(
                  "flex h-10 w-12 items-center justify-center text-center md:h-12 md:w-28",
                  isSubmitting &&
                    "cursor-wait !bg-primary-200 !outline-none !ring-0 hover:!bg-primary-200 focus:!bg-primary-200 active:!bg-primary-200"
                )}
                color={isSubmitting ? "orange-light" : "orange"}
                size="md"
              >
                {isSubmitting && (
                  <LoadingSearch className="m-auto flex aspect-[24/24] h-4 md:h-6" />
                )}
                {!isSubmitting && (
                  <div className="flex flex-row items-center gap-1">
                    <Search
                      className="text-white"
                      size={14}
                    />
                    <span className="hidden md:block">Search</span>
                  </div>
                )}
              </PrimaryButton>
            </div>
          </form>
          <div className="flex items-center gap-2 p-[2px]">
            <InlineButton
              className="!font-montserrat"
              color="gray"
              size="md"
              onClick={handleUseCurrentLocation}
            >
              <Crosshair
                className="text-neutral-500"
                size={16}
              />
              Use Current Location
            </InlineButton>
          </div>
        </div>
        {status === "success" && !isInServiceArea && size === "lg" && (
          <NoLocationNearYourSearch type="inline" />
        )}
      </div>
    </div>
  );
};

export default FindCareNearYou;
